import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { normalizeRole } from '../utils/roles';
import { readAssignments } from '../utils/companyAssignments';

const toInputDate = (value) => value ? new Date(value).toISOString().slice(0, 10) : '';
const emptyAssignment = () => ({ companyId: '', companyName: '', department: '', region: '' });

const EmployeeManagementPanel = ({ employee, roles, onClose, onSaved, onMessage }) => {
  const [form, setForm] = useState({});
  const [assignments, setAssignments] = useState([emptyAssignment()]);
  const [saving, setSaving] = useState(false);
  const [organizationFields, setOrganizationFields] = useState({ region: [], department: [] });
  const [companies, setCompanies] = useState([]);
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('userData') || '{}'); } catch (error) { return {}; } })();
  const isSuperAdmin = normalizeRole(currentUser.role) === 'super_admin';
  const isCompanyAdmin = normalizeRole(currentUser.role) === 'company_admin';
  const ownCompanyId = String(currentUser.companyId || '');
  const editableGroups = (() => {
    if (isSuperAdmin || isCompanyAdmin) return null;
    const groups = new Set();
    let open = false;
    (currentUser.customRoles || []).forEach((role) => {
      (role.permissions || []).forEach((permission) => {
        if (permission.resource !== 'employees') return;
        if (!(permission.actions || []).some((action) => action === 'edit' || action === 'manage')) return;
        if (!(permission.fields || []).length) open = true;
        (permission.fields || []).forEach((field) => groups.add(field));
      });
    });
    return open ? null : groups;
  })();
  const canEdit = (group) => !editableGroups || editableGroups.has(group);
  const canEditAssignment = (assignment) => canEdit('organization') && (isSuperAdmin || String(assignment.companyId) === ownCompanyId);

  useEffect(() => {
    if (!employee) return;
    const nextAssignments = readAssignments(employee);
    setAssignments(nextAssignments.length ? nextAssignments : [emptyAssignment()]);
    const primary = nextAssignments[0] || emptyAssignment();
    setForm({
      firstName: employee.firstName || '', lastName: employee.lastName || '', email: employee.email || '',
      company: primary.companyName || employee.company || '', companyId: primary.companyId || '', region: primary.region || '', department: primary.department || '', position: employee.position || '', employeeNumber: employee.employeeNumber || '',
      phone: employee.phone || '', extension: employee.extension || '', officeLocation: employee.officeLocation || '',
      officeBuilding: employee.officeBuilding || '', officeFloor: employee.officeFloor || '',
      startDate: toInputDate(employee.startDate), birthDate: toInputDate(employee.birthDate),
      role: employee.role || 'employee', isActive: employee.isActive !== false,
      directoryVisible: employee.visibility?.directory !== false,
      customRoles: (employee.customRoles || []).map((role) => role._id || role)
    });
  }, [employee]);

  useEffect(() => {
    api.getOrganizationFields(false, isSuperAdmin ? '' : (employee?.companyId || ''))
      .then((response) => {
        setOrganizationFields(response.data || {});
        setCompanies(response.companies || []);
      })
      .catch((error) => onMessage(error.message || 'Organizasyon alanları yüklenemedi.', 'error'));
  }, [employee?.companyId, isSuperAdmin, onMessage]);

  if (!employee) return null;

  const change = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };
  const toggleRole = (roleId) => setForm((current) => ({ ...current, customRoles: current.customRoles.includes(roleId) ? current.customRoles.filter((id) => id !== roleId) : [...current.customRoles, roleId] }));
  const updateAssignment = (index, patch) => {
    setAssignments((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };
  const fieldsFor = (type, assignment) => {
    const items = (organizationFields[type] || []).filter((item) => String(item.companyId) === String(assignment.companyId));
    if (assignment[type] && !items.some((item) => item.name === assignment[type])) {
      return [{ _id: `current-${type}-${assignment.companyId}`, name: assignment[type] }, ...items];
    }
    return items;
  };
  const companyName = (companyId, fallback) => companies.find((item) => item._id === companyId)?.name || fallback || '';

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    const companyAssignments = assignments.map((row) => ({
      companyId: row.companyId,
      department: row.department,
      region: row.region
    }));
    const primary = companyAssignments[0] || emptyAssignment();
    try {
      const response = await api.updateUser(employee._id, {
        ...form,
        companyId: primary.companyId,
        company: companyName(primary.companyId, assignments[0]?.companyName),
        department: primary.department,
        region: primary.region,
        companyAssignments
      });
      onMessage(response.message, 'success');
      onSaved(response.user);
    } catch (error) {
      onMessage(error.message || 'Çalışan güncellenemedi.', 'error');
    } finally {
      setSaving(false);
    }
  };
  const deactivate = async () => {
    setSaving(true);
    try { const response = await api.updateUser(employee._id, { isActive: !form.isActive }); onMessage(response.message, 'success'); onSaved(response.user); }
    catch (error) { onMessage(error.message, 'error'); }
    finally { setSaving(false); }
  };
  const archive = async () => {
    if (!window.confirm('Bu çalışan kaydı kalıcı olarak silinecek. Bu işlem geri alınamaz. Devam edilsin mi?')) return;
    setSaving(true);
    try { await api.permanentlyDeleteUser(employee._id); onMessage('Çalışan kaydı kalıcı olarak silindi.', 'success'); onSaved({ ...employee, _deleted: true }); onClose(); }
    catch (error) { onMessage(error.message, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="employee-panel-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="employee-panel" aria-modal="true" role="dialog" aria-labelledby="employeePanelTitle">
        <header>
          <div>
            <span>Çalışan yönetimi</span>
            <h2 id="employeePanelTitle">{employee.firstName} {employee.lastName}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Kapat"><i className="bx bx-x" /></button>
        </header>
        <form onSubmit={save}>
          <section>
            <h3>Temel bilgiler</h3>
            <div className="employee-panel-grid">
              <label><span>Çalışan no</span><input name="employeeNumber" value={form.employeeNumber || ''} onChange={change} disabled={!canEdit('employeeNumber')} /></label>
              <label><span>Ad</span><input name="firstName" value={form.firstName || ''} onChange={change} required disabled={!canEdit('identity')} /></label>
              <label><span>Soyad</span><input name="lastName" value={form.lastName || ''} onChange={change} required disabled={!canEdit('identity')} /></label>
              <label className="wide"><span>E-posta</span><input type="email" name="email" value={form.email || ''} onChange={change} required disabled={!canEdit('email')} /></label>
              <label><span>Pozisyon</span><input name="position" value={form.position || ''} onChange={change} required disabled={!canEdit('position')} /></label>
              <label><span>Telefon</span><input name="phone" value={form.phone || ''} onChange={change} disabled={!canEdit('contact')} /></label>
              <label><span>Dahili</span><input name="extension" value={form.extension || ''} onChange={change} disabled={!canEdit('contact')} /></label>
              <label className="wide"><span>Ofis / yerleşke</span><input name="officeLocation" value={form.officeLocation || ''} onChange={change} disabled={!canEdit('office')} placeholder="Örn. Ankara Genel Müdürlük" /></label>
              <label><span>Bina numarası</span><input name="officeBuilding" value={form.officeBuilding || ''} onChange={change} disabled={!canEdit('office')} placeholder="Örn. B Blok veya 12" /></label>
              <label><span>Kat</span><input name="officeFloor" value={form.officeFloor || ''} onChange={change} disabled={!canEdit('office')} placeholder="Örn. 4" /></label>
              <label><span>İşe başlama</span><input type="date" name="startDate" value={form.startDate || ''} onChange={change} disabled={!canEdit('dates')} /></label>
              <label><span>Doğum tarihi</span><input type="date" name="birthDate" value={form.birthDate || ''} onChange={change} disabled={!canEdit('dates')} /></label>
            </div>
          </section>
          <section>
            <div className="employee-assignment-heading">
              <h3>Şirket atamaları</h3>
              {isSuperAdmin && (
                <button type="button" className="role-secondary-button" onClick={() => setAssignments((current) => [...current, emptyAssignment()])} disabled={!canEdit('organization')}>
                  Şirket ekle
                </button>
              )}
            </div>
            <p className="employee-assignment-note">Her şirket için ayrı bölge ve departman seçilir. İlk satır çalışanın birincil firmasıdır.</p>
            <div className="employee-assignments">
              {assignments.map((assignment, index) => {
                const editable = canEditAssignment(assignment);
                const companyLocked = !isSuperAdmin || !canEdit('organization');
                return (
                  <div className="employee-assignment-row" key={`${assignment.companyId}-${index}`}>
                    <label>
                      <span>Şirket</span>
                      <select
                        aria-label={`Şirket ${index + 1}`}
                        value={assignment.companyId}
                        required
                        disabled={companyLocked}
                        onChange={(event) => {
                          const nextCompany = companies.find((item) => item._id === event.target.value);
                          updateAssignment(index, {
                            companyId: event.target.value,
                            companyName: nextCompany?.name || '',
                            department: '',
                            region: ''
                          });
                        }}
                      >
                        {!assignment.companyId && <option value="">Seçin</option>}
                        {assignment.companyId && !companies.some((item) => item._id === assignment.companyId) && (
                          <option value={assignment.companyId}>{assignment.companyName || 'Şirket'}</option>
                        )}
                        {companies.map((item) => <option key={item._id} value={item._id}>{item.name}</option>)}
                      </select>
                    </label>
                    <label>
                      <span>Bölge</span>
                      <select aria-label={`Bölge ${index + 1}`} value={assignment.region} required={editable} disabled={!editable} onChange={(event) => updateAssignment(index, { region: event.target.value })}>
                        <option value="">Seçin</option>
                        {fieldsFor('region', assignment).map((item) => <option key={item._id} value={item.name}>{item.name}</option>)}
                      </select>
                    </label>
                    <label>
                      <span>Departman</span>
                      <select aria-label={`Departman ${index + 1}`} value={assignment.department} required={editable} disabled={!editable} onChange={(event) => updateAssignment(index, { department: event.target.value })}>
                        <option value="">Seçin</option>
                        {fieldsFor('department', assignment).map((item) => <option key={item._id} value={item.name}>{item.name}</option>)}
                      </select>
                    </label>
                    {isSuperAdmin && assignments.length > 1 && (
                      <button type="button" className="role-danger-button" onClick={() => setAssignments((current) => current.filter((_, rowIndex) => rowIndex !== index))} disabled={!canEdit('organization')}>
                        Kaldır
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
          <section>
            <h3>Erişim ve durum</h3>
            <div className="employee-panel-grid">
              <label>
                <span>Sistem seviyesi</span>
                <select name="role" value={form.role || 'employee'} onChange={change} disabled={!canEdit('role')}>
                  <option value="employee">Çalışan</option>
                  <option value="company_admin">Admin</option>
                  {isSuperAdmin && <option value="super_admin">Süper Admin</option>}
                </select>
              </label>
              <label className="employee-switch">
                <input type="checkbox" name="directoryVisible" checked={Boolean(form.directoryVisible)} onChange={change} disabled={!canEdit('status')} />
                <span>Rehberde görünür</span>
              </label>
            </div>
            {roles.length > 0 && (
              <div className="employee-role-options">
                <p>Özel roller</p>
                {roles.map((role) => (
                  <label key={role._id}>
                    <input type="checkbox" checked={(form.customRoles || []).includes(role._id)} onChange={() => toggleRole(role._id)} disabled={!canEdit('role')} />
                    <span className="role-dot" style={{ background: role.color }} />
                    {role.name}
                  </label>
                ))}
              </div>
            )}
          </section>
          <footer>
            <div>
              {isSuperAdmin && <button type="button" className="role-danger-button" onClick={archive} disabled={saving}>Kalıcı sil</button>}
              <button type="button" className="role-secondary-button" onClick={deactivate} disabled={saving}>{form.isActive ? 'Pasife al' : 'Aktifleştir'}</button>
            </div>
            <button className="role-primary-button" type="submit" disabled={saving}>{saving ? 'Kaydediliyor…' : 'Değişiklikleri kaydet'}</button>
          </footer>
        </form>
      </aside>
    </div>
  );
};

export default EmployeeManagementPanel;
